import React, { type ComponentProps, type ReactNode, useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { useTranslation } from 'next-i18next';
import { ZepquizLogoIcon } from '@/icons';
import { usePlayUrl } from '@/shared/hooks';
import { cn } from '@/utils';
import { PAGE_UI_CONFIG } from './const';
import { InvisiblePageChunkGenerator } from './InvisiblePageChunkGenerator';
import { QuizWorksheetTwoColumnLayout } from './QuizWorksheetTwoColumnLayout';
import { type AnswerPageBlockData, type BlockData, type QuestionPageBlockData } from './type';
export const QUIZ_WORKSHEET_PAGE_CLASS_NAME = 'quiz-worksheet-page';
export const QUIZ_WORKSHEET_PAGE_TITLE_AREA_CLASS_NAME = 'quiz-worksheet-page-title-area';
const QuizWorksheetPageHeader = ({
  worksheetTitle,
  worksheetTeacherName,
  worksheetSubTitle
}: {
  worksheetTitle: string;
  worksheetTeacherName: string;
  worksheetSubTitle: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="border-b border-alternative pb-2xs pt-xs" data-sentry-component="QuizWorksheetPageHeader" data-sentry-source-file="QuizWorksheetPage.tsx">
      <div className="flex flex-row justify-between px-xs text-[9px] font-[500] leading-[15.4px] text-assistive">
        <span>{`${t('common:common.name')} :`}</span>
        <span>{worksheetTeacherName}</span>
      </div>
      <div className={cn(QUIZ_WORKSHEET_PAGE_TITLE_AREA_CLASS_NAME, 'flex flex-col gap-4xs')}>
        <h3 className="text-center text-body-md-bold text-default">
          {worksheetTitle}
        </h3>
        <h1 className="text-center text-heading-xl-extrabold text-default">
          {worksheetSubTitle}
        </h1>
      </div>
    </div>;
};
type QuizWorksheetPagesProps<T> = {
  header?: {
    title: string;
    teacherName: string;
    subTitle: string;
  };
  footer?: {
    enterCode?: number;
    entryMapHashId: string;
    startPageNumber?: number;
  };
  blocks: T[];
  itemRenderer: React.FC<{
    props: T;
  }>;
  onPageRenderComplete?: (pageNumber: number) => void;
};
const QuizWorksheetPages = <T extends BlockData<string, unknown>,>({
  header,
  footer,
  blocks,
  itemRenderer,
  onPageRenderComplete
}: QuizWorksheetPagesProps<T>) => {
  const [pages, setPages] = useState<{
    leftColumn: ReactNode[];
    rightColumn: ReactNode[];
  }[]>([]);
  const handleColumnChunkGenerateComplete = useCallback((pagesWithTwoColumns: {
    leftColumn: ReactNode[];
    rightColumn: ReactNode[];
  }[]) => {
    setPages(pagesWithTwoColumns);
  }, [setPages]);
  useEffect(() => {
    if (onPageRenderComplete) {
      onPageRenderComplete(pages.length);
    }
  }, [onPageRenderComplete, pages]);
  return <div className="relative mx-auto flex w-fit flex-col gap-sm bg-transparent" style={{
    width: `${PAGE_UI_CONFIG.width}px`
  }} data-sentry-component="QuizWorksheetPages" data-sentry-source-file="QuizWorksheetPage.tsx">
      <InvisiblePageChunkGenerator blocks={blocks} renderer={itemRenderer} onGenerateComplete={handleColumnChunkGenerateComplete} data-sentry-element="InvisiblePageChunkGenerator" data-sentry-source-file="QuizWorksheetPage.tsx" />

      {pages.map((page, index) => <QuizWorksheetTwoColumnLayout key={index} pageNumber={(footer?.startPageNumber ?? 1) + index} leftColumnItems={page.leftColumn} rightColumnItems={page.rightColumn} header={header && <QuizWorksheetPageHeader worksheetTitle={header.title} worksheetTeacherName={header.teacherName} worksheetSubTitle={header.subTitle} />} footer={footer && <QuizWorksheetPageFooter enterCode={footer.enterCode} pageNumber={(footer.startPageNumber ?? 1) + index} entryMapHashId={footer.entryMapHashId} />} />)}
    </div>;
};
export const QUIZ_WORKSHEET_PAGE_FOOTER_LOGO_CLASS_NAME = 'quiz-worksheet-page-footer-logo';
export const QUIZ_WORKSHEET_PAGE_FOOTER_QR_CODE_CLASS_NAME = 'quiz-worksheet-page-footer-qr-code';
const QuizWorksheetPageFooter = ({
  enterCode,
  pageNumber,
  entryMapHashId
}: {
  enterCode?: number;
  pageNumber: number;
  entryMapHashId: string;
}) => {
  const playUrl = usePlayUrl(entryMapHashId);
  const {
    t
  } = useTranslation(['common']);
  return <div className="relative flex flex-row justify-between" data-sentry-component="QuizWorksheetPageFooter" data-sentry-source-file="QuizWorksheetPage.tsx">
      <span className="flex flex-row items-end gap-3xs">
        <span className={QUIZ_WORKSHEET_PAGE_FOOTER_LOGO_CLASS_NAME}>
          <ZepquizLogoIcon className="size-[14px]" height={14} width={14} data-sentry-element="ZepquizLogoIcon" data-sentry-source-file="QuizWorksheetPage.tsx" />
        </span>
        <span className="text-[8px] font-[500] leading-[15.4px] text-assistive">
          {t('common:quizWorksheet.madeByZepquiz')}
        </span>
      </span>
      {!!enterCode && <div className="flex flex-row items-end gap-2xs">
          <span className="inline-block h-md text-[8px] font-[600] leading-[15.4px] text-neutral">
            {t('common:quizWorksheet.enterCode', {
          enterCode
        })}
          </span>
          <span className={cn(QUIZ_WORKSHEET_PAGE_FOOTER_QR_CODE_CLASS_NAME, 'pr-xs')}>
            <QRCode value={playUrl} size={30} />
          </span>
        </div>}
      {!!pageNumber && <span className="absolute inline-block w-full self-end text-center text-[9px] font-[400] leading-[15.4px]">
          {`(${pageNumber})`}
        </span>}
    </div>;
};
const QuizWorksheetQuestionPages = ({
  ...props
}: ComponentProps<typeof QuizWorksheetPages<QuestionPageBlockData>>) => {
  return <QuizWorksheetPages<QuestionPageBlockData> {...props} data-sentry-element="QuizWorksheetPages" data-sentry-component="QuizWorksheetQuestionPages" data-sentry-source-file="QuizWorksheetPage.tsx" />;
};
const QuizWorksheetAnswerPages = ({
  ...props
}: ComponentProps<typeof QuizWorksheetPages<AnswerPageBlockData>>) => {
  return <QuizWorksheetPages<AnswerPageBlockData> {...props} data-sentry-element="QuizWorksheetPages" data-sentry-component="QuizWorksheetAnswerPages" data-sentry-source-file="QuizWorksheetPage.tsx" />;
};
export { QuizWorksheetQuestionPages, QuizWorksheetAnswerPages };